import { modifier } from 'ember-modifier';

/**
 * in-view modifier
 *
 * Light-weight IntersectionObserver-based utility that toggles an `in-view` CSS class
 * on the element when it enters the viewport, with optional one-shot behavior and
 * per-element delay. Also exposes a CSS custom property `--animation-delay` so you
 * can reference it in your CSS transitions/animations.
 *
 * Usage example (Glimmer template):
 * <div
 *   {{inView threshold=0.15 rootMargin="0px 0px -10% 0px" once=true delay=80
 *           onEnter=(fn this.handleEnter) onLeave=(fn this.handleLeave)}}
 * >
 *   ...
 * </div>
 *
 * Behavior:
 * - When the element intersects, we add class `in-view` and set
 *   `--animation-delay: <delay>ms` on the element style.
 * - If `once=true`, the observer runs only the first time it intersects.
 * - If `once=false`, we remove the class on exit and call `onLeave` if provided.
 *
 * Options (named args):
 * - threshold: number | number[] (default 0.1) - IntersectionObserver threshold.
 * - rootMargin: string (default '0px') - Observer root margin.
 * - onEnter: (el: Element) => void - Callback fired when enters view.
 * - onLeave: (el: Element) => void - Callback fired when leaves view (only when once=false).
 * - once: boolean (default true) - If true, triggers only the first time.
 * - delay: number (default 0) - Milliseconds to delay; exported to CSS var and class timing.
 */

export default modifier((element, _positional, options = {}) => {
  const {
    threshold = 0.1,
    rootMargin = '0px',
    onEnter,
    onLeave,
    once = true,
    delay = 0
  } = options;

  let hasTriggered = false;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        if (!hasTriggered || !once) {
          // Establecer el delay como CSS custom property
          element.style.setProperty('--animation-delay', `${delay}ms`);
          element.classList.add('in-view');
          if (onEnter) {
            onEnter(element);
          }
          if (once) {
            hasTriggered = true;
          }
        }
      } else {
        if (!once) {
          element.classList.remove('in-view');
          if (onLeave) {
            onLeave(element);
          }
        }
      }
    });
  }, {
    threshold,
    rootMargin
  });

  observer.observe(element);

  return () => {
    observer.disconnect();
  };
});
